/* ============================================================================
 * OSWindow — the `.os-window` shell
 * ----------------------------------------------------------------------------
 * Draggable + resizable by pointer events (mouse, touch, pen — one code path).
 * Drag and resize deltas are applied to a local rect and committed on release,
 * so a fast drag cannot outrun React state.
 * ========================================================================= */

import { useCallback, useRef, type ReactNode } from 'react';
import type { OSWindowState } from '../hooks/useWindowManager';
import { appById } from '../apps/registry';
import { SkinArt } from './SkinArt';

interface Props {
  state: OSWindowState;
  active: boolean;
  onFocus: () => void;
  onClose: () => void;
  onMinimize: () => void;
  onMove: (x: number, y: number) => void;
  onResize: (w: number, h: number) => void;
  children: ReactNode;
}

interface DragOrigin {
  pointerId: number;
  startX: number;
  startY: number;
  rectX: number;
  rectY: number;
}

/**
 * Pointer capture is best-effort. It throws NotFoundError when the pointer is
 * not currently active (synthetic events, some pen/touch stacks, and pointers
 * released between events). A failed capture must never abort handler setup —
 * otherwise the drag/resize gesture silently dies on the first event.
 */
function capturePointer(el: Element, pointerId: number): void {
  try {
    (el as HTMLElement).setPointerCapture(pointerId);
  } catch {
    /* no active pointer with that id — continue without capture */
  }
}

export function OSWindow({
  state,
  active,
  onFocus,
  onClose,
  onMinimize,
  onMove,
  onResize,
  children,
}: Props) {
  const { id, title, x, y, w, h, minimized } = state;
  const app = appById(state.appId);
  const dragRef = useRef<DragOrigin | null>(null);
  const resizeRef = useRef<DragOrigin | null>(null);

  const clampX = useCallback(
    (nx: number, nw: number) =>
      Math.max(-(nw - 80), Math.min(nx, window.innerWidth - 60)),
    [],
  );
  const clampY = useCallback(
    (ny: number) =>
      Math.max(0, Math.min(ny, window.innerHeight - 40)),
    [],
  );

  const onTitlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('.os-window__controls')) return;
      onFocus();
      capturePointer(e.currentTarget, e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        rectX: x,
        rectY: y,
      };
    },
    [onFocus, x, y],
  );

  const onTitlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      onMove(
        clampX(d.rectX + (e.clientX - d.startX), w),
        clampY(d.rectY + (e.clientY - d.startY)),
      );
    },
    [clampX, clampY, onMove, w],
  );

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
  }, []);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onFocus();
      capturePointer(e.currentTarget, e.pointerId);
      resizeRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        rectX: w,
        rectY: h,
      };
    },
    [onFocus, w, h],
  );

  const onResizePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = resizeRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      onResize(
        d.rectX + (e.clientX - d.startX),
        d.rectY + (e.clientY - d.startY),
      );
    },
    [onResize],
  );

  const endResize = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (resizeRef.current?.pointerId === e.pointerId) resizeRef.current = null;
  }, []);

  return (
    <section
      className="os-window wos-crt"
      style={{ left: x, top: y, width: w, height: h, zIndex: active ? 60 + state.z : state.z }}
      data-active={active}
      data-minimized={minimized}
      data-window-id={id}
      role="dialog"
      aria-label={title}
      onPointerDown={onFocus}
    >
      <header
        className="os-window__titlebar"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {app && (
          <SkinArt
            className="os-window__icon pix"
            src={app.tile}
            width={14}
            height={14}
            alt=""
            aria-hidden="true"
          />
        )}
        <span className="os-window__title">{title}</span>
        <span className="os-window__controls">
          <button
            type="button"
            className="os-window__btn"
            title="Minimize"
            aria-label={`Minimize ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              onMinimize();
            }}
          >
            _
          </button>
          <button
            type="button"
            className="os-window__btn os-window__btn--close"
            title="Close"
            aria-label={`Close ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            X
          </button>
        </span>
      </header>

      <div className="os-window__body">{children}</div>

      <div
        className="os-window__resize"
        title="Resize"
        aria-hidden="true"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
      />
    </section>
  );
}
